// iter-274 — Invoice Service Catalog (main + sub) with multi-platform picker.
import { useCallback, useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Edit3, Trash2, Loader2, X, Check, Bot, ChevronDown, ChevronRight, ListChecks,
} from 'lucide-react';
import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InvoiceServiceCatalogPage() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [rows, setRows] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIds, setOpenIds] = useState({});

  const [modalOpen, setModalOpen] = useState(false);
  const [editingMain, setEditingMain] = useState(null);
  const [draft, setDraft] = useState({
    name_ar: '', name_en: '',
    platforms: [],  // empty = all
    sub_services: [{ name_ar: '', name_en: '' }],
  });
  // iter-283 (Feb 2026) — original sub-services snapshot (by id) captured
  // when opening the edit modal. Used at save time to figure out which
  // subs were removed / renamed / added.
  const [originalSubs, setOriginalSubs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [translatingIdx, setTranslatingIdx] = useState(null);

  const loadPlatforms = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/service-catalog/meta/platforms`,
        { headers: getAuthHeaders() });
      setPlatforms(r.data?.data || []);
    } catch { toast.error(t('فشل تحميل المنصات', 'Failed to load platforms')); }
  }, [isAr]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/service-catalog/invoice`,
        { headers: getAuthHeaders() });
      setRows(r.data?.data || []);
    } catch { toast.error(t('فشل التحميل', 'Load failed')); }
    finally { setLoading(false); }
  }, [isAr]);

  useEffect(() => { loadPlatforms(); load(); }, [loadPlatforms, load]);

  const openAdd = () => {
    setEditingMain(null);
    setDraft({ name_ar: '', name_en: '', platforms: [], sub_services: [{ name_ar: '', name_en: '' }] });
    setOriginalSubs([]);
    setModalOpen(true);
  };

  const openEdit = (main) => {
    setEditingMain(main);
    // iter-283 (Feb 2026) — Bug fix: sub-services were previously hidden
    // AND skipped during edit — the operator couldn't add / edit / remove
    // sub-services on an already-saved main service. We now preload the
    // existing subs (each with its `id`) so the save logic can diff:
    //   * new rows (no id)                   → POST /sub
    //   * existing rows with changed name    → PATCH /sub/{sub_id}
    //   * previously-present rows now missing → DELETE /sub/{sub_id}
    const seededSubs = main.sub_services?.length
      ? main.sub_services.map(s => ({
          id: s.id,                     // presence == existing on the server
          name_ar: s.name_ar || '',
          name_en: s.name_en || '',
        }))
      : [{ name_ar: '', name_en: '' }];
    setDraft({
      name_ar: main.name_ar || '',
      name_en: main.name_en || '',
      platforms: main.platforms || [],
      sub_services: seededSubs,
    });
    setOriginalSubs(seededSubs.filter(s => s.id));  // snapshot for diff
    setModalOpen(true);
  };

  const translate = async (text, direction) => {
    if (!text.trim()) return null;
    try {
      const r = await axios.post(`${API_URL}/api/translate`, { text, direction },
        { headers: getAuthHeaders() });
      return r.data?.translated || null;
    } catch { toast.error(t('فشل الترجمة', 'Translation failed')); return null; }
  };

  const translateMain = async () => {
    if (draft.name_ar && !draft.name_en) {
      setTranslatingIdx(-1);
      const t2 = await translate(draft.name_ar, 'ar_to_en');
      if (t2) setDraft(d => ({ ...d, name_en: t2 }));
      setTranslatingIdx(null);
    } else if (draft.name_en && !draft.name_ar) {
      setTranslatingIdx(-1);
      const t2 = await translate(draft.name_en, 'en_to_ar');
      if (t2) setDraft(d => ({ ...d, name_ar: t2 }));
      setTranslatingIdx(null);
    } else {
      toast.error(t('اكتب الاسم بلغة واحدة فقط للترجمة', 'Enter one language only'));
    }
  };

  const translateSub = async (idx) => {
    const s = draft.sub_services[idx];
    if (s.name_ar && !s.name_en) {
      setTranslatingIdx(idx);
      const t2 = await translate(s.name_ar, 'ar_to_en');
      if (t2) {
        const arr = [...draft.sub_services]; arr[idx] = { ...arr[idx], name_en: t2 };
        setDraft(d => ({ ...d, sub_services: arr }));
      }
      setTranslatingIdx(null);
    } else if (s.name_en && !s.name_ar) {
      setTranslatingIdx(idx);
      const t2 = await translate(s.name_en, 'en_to_ar');
      if (t2) {
        const arr = [...draft.sub_services]; arr[idx] = { ...arr[idx], name_ar: t2 };
        setDraft(d => ({ ...d, sub_services: arr }));
      }
      setTranslatingIdx(null);
    }
  };

  const togglePlatform = (pid) => {
    setDraft(d => {
      const has = d.platforms.includes(pid);
      return { ...d, platforms: has ? d.platforms.filter(x => x !== pid) : [...d.platforms, pid] };
    });
  };

  const selectAllPlatforms = () => setDraft(d => ({ ...d, platforms: [] }));

  const addSubRow = () =>
    setDraft(d => ({ ...d, sub_services: [...d.sub_services, { name_ar: '', name_en: '' }] }));

  const removeSubRow = (idx) => {
    if (draft.sub_services.length <= 1) {
      toast.error(t('يجب إبقاء خدمة فرعية واحدة على الأقل', 'Must keep at least one sub-service'));
      return;
    }
    setDraft(d => ({ ...d, sub_services: d.sub_services.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    const nameAr = draft.name_ar.trim();
    const nameEn = draft.name_en.trim();
    if (!nameAr) { toast.error(t('اكتب الاسم بالعربي', 'Enter Arabic name')); return; }
    // Keep both trimmed name AND the id (if present) so we can distinguish
    // existing (server-side) rows from new drafts during the diff.
    const subs = draft.sub_services
      .filter(s => (s.name_ar || '').trim() || (s.name_en || '').trim())
      .map(s => ({
        ...(s.id ? { id: s.id } : {}),
        name_ar: (s.name_ar || '').trim(),
        name_en: (s.name_en || '').trim(),
      }));
    if (subs.length < 1) { toast.error(t('أضف خدمة فرعية واحدة على الأقل', 'Add at least one sub-service')); return; }
    setSaving(true);
    try {
      const payload = { name_ar: nameAr, name_en: nameEn, platforms: draft.platforms };
      if (editingMain) {
        // 1) update the main service (name + platforms).
        await axios.patch(`${API_URL}/api/admin/service-catalog/invoice/${editingMain.id}`,
          payload, { headers: getAuthHeaders() });

        // iter-283 — sync sub-services in three passes.
        // (a) DELETE sub-services that were present originally but are
        //     no longer in the current draft.
        const currentIds = new Set(subs.filter(s => s.id).map(s => s.id));
        const removedIds = originalSubs
          .map(s => s.id)
          .filter(id => id && !currentIds.has(id));
        for (const rmId of removedIds) {
          await axios.delete(
            `${API_URL}/api/admin/service-catalog/invoice/${editingMain.id}/sub/${rmId}`,
            { headers: getAuthHeaders() },
          );
        }

        // (b) PATCH existing subs whose name changed.
        for (const s of subs) {
          if (!s.id) continue;
          const orig = originalSubs.find(o => o.id === s.id);
          if (!orig) continue;
          if (orig.name_ar !== s.name_ar || orig.name_en !== s.name_en) {
            await axios.patch(
              `${API_URL}/api/admin/service-catalog/invoice/${editingMain.id}/sub/${s.id}`,
              { name_ar: s.name_ar, name_en: s.name_en },
              { headers: getAuthHeaders() },
            );
          }
        }

        // (c) POST newly-added subs (no id).
        for (const s of subs) {
          if (s.id) continue;
          await axios.post(
            `${API_URL}/api/admin/service-catalog/invoice/${editingMain.id}/sub`,
            { name_ar: s.name_ar, name_en: s.name_en },
            { headers: getAuthHeaders() },
          );
        }

        toast.success(t('تم الحفظ', 'Saved'));
      } else {
        // Fresh main + all subs in one shot.
        await axios.post(`${API_URL}/api/admin/service-catalog/invoice`,
          { ...payload, sub_services: subs.map(({ name_ar, name_en }) => ({ name_ar, name_en })) },
          { headers: getAuthHeaders() });
        toast.success(t('تمت الإضافة', 'Added'));
      }
      setModalOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally { setSaving(false); }
  };

  const deleteMain = async (id) => {
    if (!window.confirm(t('حذف الخدمة الرئيسية وكل الفرعية؟', 'Delete main + all subs?'))) return;
    try {
      await axios.delete(`${API_URL}/api/admin/service-catalog/invoice/${id}`,
        { headers: getAuthHeaders() });
      toast.success(t('تم الحذف', 'Deleted')); load();
    } catch (e) { toast.error(e.response?.data?.detail || t('فشل الحذف', 'Delete failed')); }
  };

  const deleteSub = async (mainId, subId) => {
    if (!window.confirm(t('حذف هذه الخدمة الفرعية؟', 'Delete this sub-service?'))) return;
    try {
      await axios.delete(`${API_URL}/api/admin/service-catalog/invoice/${mainId}/sub/${subId}`,
        { headers: getAuthHeaders() });
      toast.success(t('تم الحذف', 'Deleted')); load();
    } catch (e) { toast.error(e.response?.data?.detail || t('فشل الحذف', 'Delete failed')); }
  };

  const platformLabel = (id) => {
    const p = platforms.find(x => x.id === id);
    return p ? (isAr ? p.ar : p.en) : id;
  };

  return (
    <div className="min-h-screen bg-[#030303]" dir={isAr ? 'rtl' : 'ltr'}>
      <DashboardHeader showBackButton backPath="/dashboard/finance/settings/dropdown-lists" />

      <div className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' },
            { label_ar: 'إعدادات القسم المالي', label_en: 'Finance Settings', path: '/dashboard/finance/settings' },
            { label_ar: 'القوائم المنسدلة', label_en: 'Dropdowns', path: '/dashboard/finance/settings/dropdown-lists' },
            { label_ar: 'قوائم إنشاء الفاتورة (رئيسي + فرعي)', label_en: 'Invoice Dropdowns (Main + Sub)', path: null },
          ]} />
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-2">
              <ListChecks className="w-7 h-7 text-[#d4af37]" />
              {t('قوائم إنشاء الفاتورة', 'Invoice Creation Dropdowns')}
            </h1>
            <p className="text-white/50 text-sm">
              {t('اسم الخدمة الرئيسية + الفرعية، مع اختيار المنصات المستهدفة.', 'Main + sub service names with target platform picker.')}
            </p>
          </div>
          <Button onClick={openAdd} className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-black" data-testid="inv-cat-add">
            <Plus className="w-4 h-4 me-1" />{t('إضافة خدمة رئيسية', 'Add main service')}
          </Button>
        </div>

        {loading ? (
          <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-white/40 inline" /></div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-white/40 border border-white/5 rounded-xl">
            {t('لا توجد خدمات بعد. أضف الأولى من الأعلى.', 'No services yet. Add your first.')}
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((main) => {
              const isOpen = !!openIds[main.id];
              const plats = main.platforms || [];
              const allPlatforms = plats.length === 0;
              return (
                <div key={main.id} className="bg-white/[0.02] border border-white/10 rounded-xl overflow-hidden"
                     data-testid={`inv-cat-row-${main.id}`}>
                  <div className="flex items-center gap-3 p-4 hover:bg-white/[0.02]">
                    <button onClick={() => setOpenIds(o => ({ ...o, [main.id]: !isOpen }))}
                            className="p-1 text-white/60">
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    <div className="flex-1">
                      <div className="text-white font-semibold" dir="rtl">{main.name_ar}</div>
                      <div className="text-white/50 text-xs" dir="ltr">{main.name_en}</div>
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {allPlatforms ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] bg-emerald-500/15 text-emerald-300">
                            {t('كل المنصات', 'All platforms')}
                          </span>
                        ) : plats.map(p => (
                          <span key={p} className="px-2 py-0.5 rounded-full text-[10px] bg-sky-500/15 text-sky-300">
                            {platformLabel(p)}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-[11px] text-white/50">
                      {t('الفرعية:', 'Subs:')} <b className="text-white/80">{main.sub_services?.length || 0}</b>
                    </div>
                    <button onClick={() => openEdit(main)}
                            className="p-1.5 rounded-md hover:bg-white/5 text-white/60"
                            data-testid={`inv-cat-edit-${main.id}`}
                            title={t('تعديل', 'Edit')}>
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button onClick={() => deleteMain(main.id)}
                            className="p-1.5 rounded-md hover:bg-red-500/10 text-red-400/60 hover:text-red-400"
                            data-testid={`inv-cat-delete-${main.id}`}
                            title={t('حذف', 'Delete')}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {isOpen && (
                    <div className="border-t border-white/5 bg-black/30 p-3 space-y-1.5">
                      {main.sub_services?.map((s) => (
                        <div key={s.id} className="flex items-center gap-3 px-3 py-2 rounded-md bg-white/[0.02]">
                          <div className="flex-1">
                            <div className="text-white text-sm" dir="rtl">{s.name_ar}</div>
                            <div className="text-white/40 text-xs" dir="ltr">{s.name_en || '—'}</div>
                          </div>
                          <button onClick={() => deleteSub(main.id, s.id)}
                                  className="p-1 rounded-md hover:bg-red-500/10 text-red-400/60 hover:text-red-400">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
               dir={isAr ? 'rtl' : 'ltr'} data-testid="inv-cat-modal">
            <div className="sticky top-0 bg-[#0a0a0a] border-b border-white/10 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">
                {editingMain ? t('تعديل الخدمة الرئيسية', 'Edit main service') : t('إضافة خدمة رئيسية', 'Add main service')}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-white/60 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {/* Main name */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-white/70 text-sm font-medium">{t('اسم الخدمة الرئيسية', 'Main service name')}</label>
                  <Button size="sm" variant="ghost" onClick={translateMain}
                          disabled={translatingIdx !== null}
                          className="h-7 px-2 text-[11px] text-cyan-300 hover:bg-cyan-500/10">
                    {translatingIdx === -1 ? <Loader2 className="w-3 h-3 animate-spin me-1" /> : <Bot className="w-3 h-3 me-1" />}
                    {t('ترجم AI', 'AI translate')}
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input value={draft.name_ar} onChange={(e) => setDraft(d => ({ ...d, name_ar: e.target.value }))}
                         placeholder="بالعربية" dir="rtl"
                         className="bg-[#050505] border-white/10 text-white" data-testid="inv-cat-main-ar" />
                  <Input value={draft.name_en} onChange={(e) => setDraft(d => ({ ...d, name_en: e.target.value }))}
                         placeholder="In English" dir="ltr"
                         className="bg-[#050505] border-white/10 text-white" data-testid="inv-cat-main-en" />
                </div>
              </div>

              {/* Platforms */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-white/70 text-sm font-medium">{t('المنصات المستهدفة', 'Target platforms')}</label>
                  <button onClick={selectAllPlatforms}
                          className="text-[11px] text-emerald-300 hover:underline">
                    {t('تحديد الكل (مشترك)', 'Select all (shared)')}
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-3 rounded-lg border border-white/10 bg-black/30">
                  {platforms.map((p) => {
                    const checked = draft.platforms.includes(p.id);
                    const isAll = draft.platforms.length === 0;
                    return (
                      <label key={p.id}
                             className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-sm
                               ${checked ? 'bg-sky-500/15 border border-sky-500/30' : 'bg-white/[0.02] border border-white/5'}`}
                             data-testid={`inv-cat-pf-${p.id}`}>
                        <input type="checkbox" checked={checked} onChange={() => togglePlatform(p.id)}
                               className="accent-sky-400" />
                        <span className="text-white/90">{isAr ? p.ar : p.en}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[11px] text-white/40 mt-2">
                  {draft.platforms.length === 0
                    ? t('ملاحظة: تركها فارغة يعني الظهور في كل المنصات.', 'Note: empty selection means visible in all platforms.')
                    : t(`اخترت ${draft.platforms.length} منصة.`, `Selected ${draft.platforms.length} platform(s).`)}
                </p>
              </div>

              {/* Sub services — iter-283 (Feb 2026): now visible during
                   edit too, with full add/rename/delete flow (previously
                   the whole section was gated by `!editingMain`). */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-white/70 text-sm font-medium">{t('الخدمات الفرعية', 'Sub-services')}</label>
                  <Button size="sm" onClick={addSubRow}
                          className="h-7 px-2 bg-white/5 hover:bg-white/10 text-white/80"
                          data-testid="inv-cat-add-sub-btn">
                    <Plus className="w-3 h-3 me-1" />{t('إضافة فرعية', 'Add sub')}
                  </Button>
                </div>
                <div className="space-y-2">
                  {draft.sub_services.map((s, idx) => (
                    <div key={s.id || `new-${idx}`}
                         className="p-3 rounded-lg border border-white/10 bg-white/[0.02]"
                         data-testid={`inv-cat-sub-row-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] text-white/50">
                          {t(`خدمة فرعية #${idx + 1}`, `Sub #${idx + 1}`)}
                          {s.id && (
                            <span className="ms-2 text-[10px] text-emerald-400/70">
                              {t('محفوظة', 'saved')}
                            </span>
                          )}
                          {!s.id && editingMain && (
                            <span className="ms-2 text-[10px] text-amber-400/70">
                              {t('جديدة', 'new')}
                            </span>
                          )}
                        </span>
                        <div className="flex items-center gap-2">
                          <button onClick={() => translateSub(idx)} disabled={translatingIdx !== null}
                                  className="text-[10px] text-cyan-300 hover:underline">
                            {translatingIdx === idx ? <Loader2 className="w-3 h-3 inline animate-spin me-1" /> : <Bot className="w-3 h-3 inline me-1" />}
                            {t('ترجم', 'Translate')}
                          </button>
                          {draft.sub_services.length > 1 && (
                            <button onClick={() => removeSubRow(idx)}
                                    className="text-red-400/70 hover:text-red-400"
                                    data-testid={`inv-cat-sub-remove-${idx}`}>
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={s.name_ar} onChange={(e) => {
                                const arr = [...draft.sub_services];
                                arr[idx] = { ...arr[idx], name_ar: e.target.value };
                                setDraft(d => ({ ...d, sub_services: arr }));
                              }} placeholder="بالعربية" dir="rtl"
                              className="bg-[#050505] border-white/10 text-white h-9 text-sm"
                              data-testid={`inv-cat-sub-ar-${idx}`} />
                        <Input value={s.name_en} onChange={(e) => {
                                const arr = [...draft.sub_services];
                                arr[idx] = { ...arr[idx], name_en: e.target.value };
                                setDraft(d => ({ ...d, sub_services: arr }));
                              }} placeholder="In English" dir="ltr"
                              className="bg-[#050505] border-white/10 text-white h-9 text-sm"
                              data-testid={`inv-cat-sub-en-${idx}`} />
                      </div>
                    </div>
                  ))}
                </div>
                {editingMain && (
                  <p className="text-[11px] text-white/40 mt-2">
                    {t('التغييرات على الفرعية تُحفظ عند الضغط على "حفظ".',
                       'Sub-service changes are persisted when you press "Save".')}
                  </p>
                )}
              </div>
            </div>
            <div className="sticky bottom-0 bg-[#0a0a0a] border-t border-white/10 px-6 py-4 flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={() => setModalOpen(false)}
                      className="text-white/70 hover:bg-white/5">{t('إلغاء', 'Cancel')}</Button>
              <Button onClick={save} disabled={saving}
                      className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-black"
                      data-testid="inv-cat-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin me-1" /> : <Check className="w-4 h-4 me-1" />}
                {editingMain ? t('حفظ', 'Save') : t('إضافة', 'Add')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
